import { defineStore } from 'pinia';
import { ref } from 'vue';
import { TranslationService } from '@/services/translation.services';
import type {
  ITranslationExportParams,
  ITranslationImportReport,
} from '@/types/translation.types';

export const useTranslationStore = defineStore('translation', () => {
  const isExporting = ref(false);
  const isImporting = ref(false);
  const uploadProgress = ref(0);
  const importReport = ref<ITranslationImportReport | null>(null);

  const exportTranslations = async (
    params?: Partial<ITranslationExportParams>,
  ) => {
    try {
      isExporting.value = true;
      const response = await TranslationService.exportTranslations(params);
      const blob = new Blob([response.data as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'] as
        string | undefined;
      const filename =
        disposition
          ?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)?.[1]
          ?.replace(/['"]/g, '') ?? 'translations.xlsx';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      return Promise.reject(error);
    } finally {
      isExporting.value = false;
    }
  };

  const importTranslations = async (file: File) => {
    try {
      isImporting.value = true;
      uploadProgress.value = 0;
      importReport.value = null;
      const response = await TranslationService.importTranslations(
        file,
        (pct) => {
          uploadProgress.value = pct;
        },
      );
      importReport.value = response.data as ITranslationImportReport;
      return response;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      isImporting.value = false;
    }
  };

  const resetImport = () => {
    uploadProgress.value = 0;
    importReport.value = null;
  };

  return {
    isExporting,
    isImporting,
    uploadProgress,
    importReport,
    exportTranslations,
    importTranslations,
    resetImport,
  };
});
